import { useEffect, useState } from 'react';
import { ArrowRight, Layers, Wifi, MapPin } from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from './ui/button';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { currentStage } from '../data/projectStages';
import { useScrollToSection } from '../hooks/useScrollToSection';

function AnimatedCounter({ end, duration = 2 }: { end: number; duration?: number }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = (timestamp - startTime) / (duration * 1000);

      if (progress < 1) {
        setCount(Math.floor(end * progress));
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return <span>{count}</span>;
}

export function Hero() {
  const [activeImage, setActiveImage] = useState(0);
  const { scrollToSection } = useScrollToSection();

  const images = [
    'https://images.unsplash.com/photo-1758304480372-2f5e11323aeb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsb2dpc3RpY3MlMjBjZW50ZXIlMjBhZXJpYWx8ZW58MXx8fHwxNzc0ODkxODAzfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    'https://images.unsplash.com/photo-1619070284836-e850273d69ac?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwbG9naXN0aWNzfGVufDF8fHx8MTc3NDg4MzUyNHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    'https://images.unsplash.com/photo-1767639592831-d492beab8cd9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwcGFyayUyMGJ1aWxkaW5nc3xlbnwxfHx8fDE3NzQ4OTE4MDN8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % images.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden pt-20">
      <div className="absolute inset-0">
        {images.map((image, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: activeImage === index ? 1 : 0 }}
            transition={{ duration: 1 }}
            className="absolute inset-0"
          >
            <ImageWithFallback src={image} alt={`Free Zone Logistic Park ${index + 1}`} className="w-full h-full object-cover" />
          </motion.div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/88 to-slate-900/35" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(148,163,184,0.2),transparent_35%)]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="inline-flex items-center px-4 py-2 rounded-full bg-white/8 border border-white/15 text-white/90 backdrop-blur-md text-sm"
              >
                Zona franca industrial y logistica
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/8 border border-white/15 text-white/90 backdrop-blur-md"
              >
                <span className="text-xs uppercase tracking-[0.18em] text-slate-300">Etapa activa</span>
                <span className="text-sm font-medium">{currentStage.shortName}</span>
                <span className="text-xs text-slate-300">{currentStage.period}</span>
              </motion.div>
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight font-semibold leading-[1.02] mb-6"
            >
              Infraestructura logistica
              <span className="block text-slate-300 font-medium">para operaciones de escala</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg sm:text-xl text-slate-200 max-w-2xl leading-relaxed mb-8"
            >
              Un parque industrial disenado para empresas que necesitan eficiencia operativa, seguridad, conectividad y
              una ubicacion estrategica para crecer con orden.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                size="lg"
                className="bg-slate-100 text-slate-950 hover:bg-white shadow-sm"
                onClick={() => scrollToSection('contact')}
              >
                Solicitar informacion
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white/25 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                onClick={() => scrollToSection('about')}
              >
                Ver presentacion
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12"
            >
              {[
                { value: '150+', label: 'Hectareas disponibles' },
                { value: '50+', label: 'Empresas instaladas' },
                { value: '24/7', label: 'Seguridad y operacion' },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl border border-white/10 bg-white/6 backdrop-blur-md p-5" role="status" aria-label={item.label}>
                  <div className="text-2xl font-semibold text-white">{item.value}</div>
                  <div className="text-sm text-slate-300 mt-2">{item.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="relative"
          >
            <div className="rounded-[1.75rem] overflow-hidden border border-white/15 shadow-2xl bg-slate-900/40 backdrop-blur-sm">
              <div className="relative aspect-[4/5]">
                <ImageWithFallback src={images[activeImage]} alt="Vista del parque" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />

                <div className="absolute left-4 right-4 bottom-4 grid grid-cols-3 gap-2">
                  <div className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-3 py-3 flex flex-col gap-2">
                    <div className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center">
                      <Layers className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div>
                      <div className="text-white text-xl font-bold leading-none">
                        <AnimatedCounter end={150} /> ha
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">Capacidad</div>
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-3 py-3 flex flex-col gap-2">
                    <div className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center">
                      <Wifi className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div>
                      <div className="text-white text-xl font-bold leading-none">
                        <AnimatedCounter end={99} />%
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">Disponibilidad</div>
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-3 py-3 flex flex-col gap-2">
                    <div className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center">
                      <MapPin className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div>
                      <div className="text-white text-sm font-bold leading-tight">Acceso multimodal</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">Ubicación</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
